import React, { useMemo } from 'react';
import { makeStyles } from '@mui/styles';
import { Grid } from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro/DateRangePicker';
import { DateTime } from 'luxon';
import { useNoteInsightsDateRange } from 'modules/Insights/Notes/hooks/useNoteInsightsDateRange';
import { NoteInsightsViewOptions } from 'modules/Insights/Notes/NoteInsightsContextProvider';
import { Period } from 'graphql/types';
import { InsightsViewOptionsSelector } from '../InsightsViewOptionsSelector';
import { InsightsDateRangePicker } from '../InsightsDateRangePicker';
import { InsightsDateRangeArrows } from '../InsightsDateRangePicker/InsightsDateRangeArrows';

const useStyles = makeStyles(() => ({
  root: {
    alignItems: 'center'
  }
}));

type InsightsDateRangeNavigatorProps = {
  disablePrevious: boolean;
  setDisablePrevious: React.Dispatch<React.SetStateAction<boolean>>;
  disableNext: boolean;
  setDisableNext: React.Dispatch<React.SetStateAction<boolean>>;
  viewOptions: {
    value: NoteInsightsViewOptions;
    text: string;
  }[];
  currentPeriod: Period | null;
  setCurrentPeriod: (period: Period) => void;
  selectedViewOption: NoteInsightsViewOptions;
  setSelectedViewOption: (selectedViewOption: NoteInsightsViewOptions) => void;
};

const InsightsDateRangeNavigator: React.FC<InsightsDateRangeNavigatorProps> = ({
  disablePrevious,
  setDisablePrevious,
  disableNext,
  setDisableNext,
  viewOptions,
  currentPeriod,
  setCurrentPeriod,
  selectedViewOption,
  setSelectedViewOption
}: InsightsDateRangeNavigatorProps) => {
  const classes = useStyles();

  const {
    timesheetPeriodString,
    cutsomUpdateCurrentPeriod,
    updateNext,
    updatePrevious
  } = useNoteInsightsDateRange({
    currentPeriod,
    selectedViewOption,
    setCurrentPeriod,
    setSelectedViewOption,
    setDisablePrevious,
    disablePrevious,
    setDisableNext
  });
  const dateRange: DateRange<DateTime | null> = useMemo(() => {
    const _startDate = currentPeriod!.startDate
      ? DateTime.fromISO(currentPeriod!.startDate)
      : DateTime.local();
    const _endDate = currentPeriod!.endDate
      ? DateTime.fromISO(currentPeriod!.endDate)
      : DateTime.local();
    return [_startDate, _endDate];
  }, [currentPeriod]);
  return (
    <Grid
      container
      className={classes.root}
      data-testid="insights-date-range-component"
    >
      <InsightsDateRangeArrows
        disableNext={disableNext}
        disablePrevious={disablePrevious}
        updateNext={updateNext}
        updatePrevious={updatePrevious}
      />
      <InsightsDateRangePicker
        label={timesheetPeriodString}
        handleChangeDateRange={cutsomUpdateCurrentPeriod}
        value={dateRange}
      />
      <InsightsViewOptionsSelector
        setDisablePrevious={setDisablePrevious}
        setDisableNext={setDisableNext}
        disablePrevious={disablePrevious}
        viewOptions={viewOptions}
        currentPeriod={currentPeriod}
        setCurrentPeriod={setCurrentPeriod}
        setSelectedViewOption={setSelectedViewOption}
        selectedViewOption={selectedViewOption}
      />
    </Grid>
  );
};

export default React.memo(InsightsDateRangeNavigator);
